/**
 * Build a Primavera-style WBS outline from flat schedule rows.
 * Uses wbs_path ("Area > Sub-area > System") when present, else wbs_name.
 */

const NO_WBS = 'No WBS';

export function splitWbsPath(row) {
  const raw = String(row?.wbs_path || row?.wbs_name || '').trim();
  if (!raw) return [NO_WBS];
  if (raw.includes(' > ')) return raw.split(/\s*>\s*/).filter(Boolean);
  return [raw];
}

export function ancestorPaths(path) {
  if (!path) return [];
  const parts = String(path).split(/\s*>\s*/).filter(Boolean);
  const out = [];
  let acc = '';
  for (const part of parts) {
    acc = acc ? `${acc} > ${part}` : part;
    out.push(acc);
  }
  return out;
}

function toTime(value) {
  if (!value) return NaN;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : NaN;
}

function spanDays(start, finish) {
  const a = toTime(start);
  const b = toTime(finish);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '';
  return Math.max(0, Math.round((b - a) / 864e5) + 1);
}

function collectActivities(node) {
  const out = [...node.activities];
  for (const child of node.children.values()) {
    out.push(...collectActivities(child));
  }
  return out;
}

function rollup(activities) {
  let minStart = Infinity;
  let maxFinish = -Infinity;
  let pctWeight = 0;
  let weight = 0;
  let rem = 0;
  let budget_hours = 0;
  let actual_hours = 0;
  let remaining_hours = 0;
  for (const act of activities) {
    const s = toTime(act.start_date);
    const f = toTime(act.finish_date);
    if (Number.isFinite(s) && s < minStart) minStart = s;
    if (Number.isFinite(f) && f > maxFinish) maxFinish = f;
    const dur = Number(act.duration);
    const w = Number.isFinite(dur) && dur > 0 ? dur : 1;
    pctWeight += (parseFloat(act.percent_complete) || 0) * w;
    weight += w;
    rem += parseFloat(act.remaining_duration) || 0;
    budget_hours += Number(act.budget_hours) || 0;
    actual_hours += Number(act.actual_hours) || 0;
    remaining_hours += Number(act.remaining_hours ?? act.hours) || 0;
  }
  const start_date = Number.isFinite(minStart) ? new Date(minStart).toISOString().slice(0, 10) : '';
  const finish_date = Number.isFinite(maxFinish) ? new Date(maxFinish).toISOString().slice(0, 10) : '';
  return {
    start_date,
    finish_date,
    duration: spanDays(start_date, finish_date),
    percent_complete: weight ? Math.round((pctWeight / weight) * 10) / 10 : 0,
    remaining_duration: rem || '',
    budget_hours,
    actual_hours,
    remaining_hours,
  };
}

/**
 * Drop the shared P6 project-node root so the filter tree starts at real WBS bands.
 * Leaves a one-segment path (or mixed roots) alone.
 */
export function stripSharedWbsRoot(activities) {
  if (!activities?.length) return activities || [];
  const partsList = activities.map((a) => splitWbsPath(a));
  const roots = new Set(partsList.map((p) => p[0]).filter((p) => p && p !== NO_WBS));
  if (roots.size !== 1) return activities;
  const root = [...roots][0];
  const canStrip = partsList.every((parts) => parts[0] === NO_WBS || parts.length >= 2);
  if (!canStrip) return activities;
  return activities.map((a, i) => {
    const parts = partsList[i];
    if (parts[0] !== root || parts.length < 2) return a;
    return { ...a, wbs_path: parts.slice(1).join(' > ') };
  });
}

function compareValues(av, bv, direction) {
  const dir = direction === 'desc' ? -1 : 1;
  if (av == null && bv == null) return 0;
  if (av == null || av === '') return 1;
  if (bv == null || bv === '') return -1;
  if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
  if (av < bv) return -1 * dir;
  if (av > bv) return 1 * dir;
  return 0;
}

function valueForSort(row, key) {
  if (!key) return '';
  if (key === 'start_date' || key === 'finish_date') return toTime(row[key]) || 0;
  if (key === 'percent_complete' || key === 'duration' || key === 'remaining_duration') {
    const n = parseFloat(row[key]);
    return Number.isFinite(n) ? n : 0;
  }
  return String(row[key] ?? '').toLowerCase();
}

function compareByKey(a, b, key, direction) {
  if (!key) {
    return String(a.activity_id || a._label || '').localeCompare(
      String(b.activity_id || b._label || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    );
  }
  return compareValues(valueForSort(a, key), valueForSort(b, key), direction);
}

function activityIdSort(a, b) {
  return String(a.activity_id || '').localeCompare(String(b.activity_id || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * @param {Array<object>} activities
 * @param {{ sortKey?: string|null, sortDir?: string|null }} [options]
 * @returns {Array<object>}
 */
export function buildWbsOutline(activities, options = {}) {
  const { sortKey = null, sortDir = 'asc' } = options;
  const root = { children: new Map(), activities: [], path: '', label: '', level: -1 };

  const incoming = [...activities].sort(activityIdSort);
  for (const act of incoming) {
    const parts = splitWbsPath(act);
    let node = root;
    let path = '';
    parts.forEach((label, i) => {
      path = path ? `${path} > ${label}` : label;
      if (!node.children.has(path)) {
        node.children.set(path, {
          children: new Map(),
          activities: [],
          path,
          label,
          level: i,
        });
      }
      node = node.children.get(path);
    });
    node.activities.push(act);
  }

  const flatten = (node) => {
    const rows = [];
    const childNodes = [...node.children.values()];
    if (sortKey) {
      childNodes.sort((a, b) => compareByKey(rollup(collectActivities(a)), rollup(collectActivities(b)), sortKey, sortDir));
    }
    for (const child of childNodes) {
      const descendantActs = collectActivities(child);
      const rolled = rollup(descendantActs);
      rows.push({
        id: `wbs:${child.path}`,
        _kind: 'wbs',
        _level: child.level,
        _path: child.path,
        _label: child.label,
        _childActivityCount: descendantActs.length,
        _hasChildren: descendantActs.length > 0 || child.children.size > 0,
        wbs_name: child.label,
        wbs_path: child.path,
        activity_name: child.label,
        activity_id: '',
        start_date: rolled.start_date,
        finish_date: rolled.finish_date,
        duration: rolled.duration,
        remaining_duration: rolled.remaining_duration,
        percent_complete: rolled.percent_complete,
        budget_hours: rolled.budget_hours,
        actual_hours: rolled.actual_hours,
        remaining_hours: rolled.remaining_hours,
      });
      rows.push(...flatten(child));
      const acts = [...child.activities];
      acts.sort(sortKey ? (a, b) => compareByKey(a, b, sortKey, sortDir) : activityIdSort);
      for (const act of acts) {
        rows.push({
          ...act,
          _kind: 'activity',
          _level: child.level + 1,
          _path: child.path,
        });
      }
    }
    return rows;
  };

  return flatten(root);
}

export function filterCollapsedRows(rows, collapsedPaths) {
  if (!collapsedPaths || collapsedPaths.size === 0) return rows;
  return rows.filter((row) => {
    const path = row._path || '';
    const ancestors = ancestorPaths(path);
    if (row._kind === 'wbs') {
      return !ancestors.slice(0, -1).some((p) => collapsedPaths.has(p));
    }
    return !ancestors.some((p) => collapsedPaths.has(p));
  });
}

export function collectWbsPaths(rows) {
  return rows.filter((r) => r._kind === 'wbs' && r._path).map((r) => r._path);
}

export function isUnderWbsPath(rowPath, wbsPath) {
  if (!wbsPath) return false;
  const p = String(rowPath || '');
  return p === wbsPath || p.startsWith(`${wbsPath} > `);
}

/**
 * Visible-row spans for P6-style grouping rails.
 * One span per WBS that still has at least one descendant on screen.
 */
export function computeWbsGroupSpans(rows) {
  const spans = [];
  if (!Array.isArray(rows)) return spans;
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (row._kind !== 'wbs' || !row._path) continue;
    let end = i;
    for (let j = i + 1; j < rows.length; j += 1) {
      if (isUnderWbsPath(rows[j]._path, row._path)) end = j;
      else break;
    }
    if (end > i) {
      spans.push({
        path: row._path,
        level: Math.max(0, Number(row._level) || 0),
        start: i,
        end,
      });
    }
  }
  return spans;
}
